import React, { createContext, useContext, useState, useCallback } from 'react'

export interface FavoriteItem {
  id: string    // = path, used as unique key
  path: string
  label: string
}

interface FavoritesContextValue {
  favorites: FavoriteItem[]
  toggle: (item: FavoriteItem) => void
  remove: (id: string) => void
  isFavorite: (id: string) => boolean
}

const STORAGE_KEY = 'elbooks-quickbar'

function loadFromStorage(): FavoriteItem[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as FavoriteItem[]
  } catch {
    return []
  }
}

function saveToStorage(items: FavoriteItem[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null)

export function FavoritesProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [favorites, setFavorites] = useState<FavoriteItem[]>(loadFromStorage)

  const toggle = useCallback((item: FavoriteItem): void => {
    setFavorites(prev => {
      const isExisting = prev.some(f => f.id === item.id)
      const next = isExisting ? prev.filter(f => f.id !== item.id) : [...prev, item]
      saveToStorage(next)
      return next
    })
  }, [])

  const remove = useCallback((id: string): void => {
    setFavorites(prev => {
      const next = prev.filter(f => f.id !== id)
      saveToStorage(next)
      return next
    })
  }, [])

  const isFavorite = useCallback((id: string): boolean => {
    return favorites.some(f => f.id === id)
  }, [favorites])

  return (
    <FavoritesContext.Provider value={{ favorites, toggle, remove, isFavorite }}>
      {children}
    </FavoritesContext.Provider>
  )
}

export function useFavorites(): FavoritesContextValue {
  const ctx = useContext(FavoritesContext)
  if (!ctx) {throw new Error('useFavorites must be used inside FavoritesProvider')}
  return ctx
}
